import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { api } from 'shared/api/client';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { draftKeys } from 'entities/draft';
import { documentKeys } from 'entities/document';
import { styleKeys } from 'entities/style';
import { themeKeys } from 'entities/theme';
import { templateKeys } from './keys';

// These template hooks span the documents and styles contracts. The /actions
// endpoints stay loosely typed at the body level — the contract validates only
// `action` and passes the rest through — so the action payloads here are not
// checked against anything.

export function useRenameDocumentTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentSuperId, templateRevisionId, newName }: { documentSuperId: number | string; templateRevisionId: string; newName: string }) => {
      return api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentSuperId) } },
        body: {
          action: 'rename_document_template',
          templateRevisionId,
          newName: newName,
        },
      });
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.detailAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.listAll],
      });
    },
  });
}

export function useDocumentWithStyle(documentSuperId: number | string, templateId: string) {
  return useQuery({
    queryKey: templateKeys.docWithStyle(documentSuperId, templateId),

    queryFn: async () => {
      const {
        data: [doc],
      } = await api.GET('/documents/', {
        params: {
          query: {
            superId: Number(documentSuperId),
            current: 'true',
            compileSass: 'true',
            useDraft: 'true',
            useTemplate: templateId,
          },
        },
      });
      const styleId = doc.content.design?.find((s) => s.type === 'document')?.styleId;
      if (!styleId) throw new Error(`Document ${documentSuperId} has no document-type style`);
      const { data: style } = await api.GET('/styles/{id}', {
        params: { path: { id: styleId }, query: { documentRevisionId: doc.revisionId } },
      });
      return { doc, style };
    },

    enabled: !!documentSuperId && !!templateId,
    placeholderData: keepPreviousData,
  });
}

export function useApplyTemplateDraft(documentId: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ([documentRevisionId, templateId]: [string, string]) => {
      return await api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(documentId) }, query: { compileSass: 'true', useTemplate: templateId } },
        body: {
          action: 'apply_draft',
          revisionId: documentRevisionId,
        },
      });
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...draftKeys.all],
      });
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.listAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...templateKeys.detailAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.cssAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...themeKeys.appliedStatusAll],
      });
    },
  });
}
